import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Auth } from "@supabase/auth-ui-react";
import { ThemeSupa } from "@supabase/auth-ui-shared";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useTranslation } from "react-i18next";
import { AuthError, AuthApiError } from "@supabase/supabase-js";
import { LanguageToggle } from "@/components/LanguageToggle";
import { toast } from "sonner";

const SignUp = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        navigate("/opportunities");
      }
    };
    
    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("Auth state changed:", event, session);
      
      if (event === "SIGNED_IN") {
        if (session?.user?.app_metadata?.provider === "email") {
          navigate("/onboarding");
        } else {
          navigate("/opportunities");
        }
      }
      
      if (event === "USER_UPDATED") {
        const { error } = await supabase.auth.getSession();
        if (error) {
          console.error("Auth error:", error);
          setErrorMessage(getErrorMessage(error));
        }
      }
      
      if (event === "SIGNED_OUT") {
        setErrorMessage("");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const getErrorMessage = (error: AuthError) => {
    console.error("Authentication error:", error);
    
    if (error instanceof AuthApiError) {
      switch (error.message) {
        case "User already registered":
          return t("auth.userAlreadyRegistered");
        case "Invalid login credentials":
          return t("auth.invalidCredentials");
        case "Email not confirmed":
          return t("auth.emailNotConfirmed");
        default:
          if (error.message.includes("already registered")) {
            return t("auth.userAlreadyRegistered");
          }
          return error.message;
      }
    }
    return t("auth.genericError");
  };

  const handleLoginClick = () => {
    navigate("/login");
  };

  return (
    <div className="container mx-auto flex items-center justify-center min-h-screen py-8">
      <div className="w-full flex justify-between items-center absolute top-4 left-4 right-4">
        <img 
          src="/app_images/Sonho_Favicon_V2.png" 
          alt="Sonho Logo" 
          className="h-12 w-12 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => navigate('/')}
        />
        <LanguageToggle />
      </div>
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t("auth.signup")}</CardTitle>
          <CardDescription>{t("auth.signupDescription")}</CardDescription>
        </CardHeader>
        <CardContent>
          {errorMessage && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}
          <Auth
            supabaseClient={supabase}
            appearance={{
              theme: ThemeSupa,
              style: {
                button: {
                  fontWeight: '600',
                  fontSize: '1rem',
                  letterSpacing: '0.025em',
                },
              },
              variables: {
                default: {
                  colors: {
                    brand: "#ea384c",
                    brandAccent: "#ea384c",
                  },
                },
              },
            }}
            providers={[]}
            redirectTo={`${window.location.origin}/onboarding`}
            view="sign_up"
            showLinks={false}
            localization={{
              variables: {
                sign_up: {
                  button_label: t("auth.signup"),
                  email_label: t("auth.emailLabel"),
                  password_label: t("auth.passwordLabel"),
                  email_input_placeholder: t("auth.emailPlaceholder"),
                  password_input_placeholder: t("auth.passwordPlaceholder"),
                },
              },
            }}
            onlyThirdPartyProviders={false}
          />
          <div className="mt-4 text-center">
            <button
              onClick={handleLoginClick}
              className="w-full py-2 px-4 text-sm bg-white/30 backdrop-blur-sm border border-white/20 rounded-lg text-[#1A1F2C] hover:bg-white/40 transition-colors"
            >
              {t("auth.alreadyHaveAccount")}
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SignUp;